import React from "react";
import { useT } from "@/lib/LanguageContext";

/**
 * The STATED currency of a catalogue row, and the one way to change it.
 *
 * Presentment, not settlement (docs/payment-module/multi-currency-implementation-plan.md):
 * a practice charges its buyers in its OWN operating currency (`tenants.currency`), and the
 * money core refuses a NEW row denominated in anything else, so the catalogue editors state
 * that currency rather than offer a choice (the CAD/USD/EUR selector that used to sit here
 * could only ever produce a row the practice's own checkout would refuse). A row priced
 * before the practice's currency changed still holds the retired one — the server keeps it
 * on purpose (`platform_helpers.resolved_currency`), because re-denominating a price is
 * the practitioner's decision, not a side effect of editing a description. Such a row is
 * advertised on the storefront but cannot be bought online.
 *
 * So the field does two things and nothing else: it shows the currency the row is held in,
 * and — only when that differs from the practice's own — it says so and offers "Switch to
 * <denomination>". The switch is a relabel of the amount on screen; the practitioner may
 * change the amount in the same save. Nothing is sent until they save.
 *
 * `denomination` is `null` while the practice's currency is not known yet (see
 * useTenantCurrency): no notice is shown on a guess.
 *
 * @param {object} props
 * @param {string|undefined} props.currency      the row's currency as loaded (lower/upper)
 * @param {string|null}      props.denomination   the practice's OWN operating currency, lowercase
 * @param {function}         props.onSwitch       called with the practice's currency (lowercase)
 * @param {string}           props.label           translated field label
 * @param {string}           props.testid          prefix: "program" | "product"
 */
export default function CatalogueCurrencyField({ currency, denomination, onSwitch, label, testid }) {
  const t = useT("admin");
  const held = (currency || "").toLowerCase();
  const legacy = isLegacyCurrency(currency, denomination);
  const shown = (held || denomination || "").toUpperCase() || "—";
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
      <div
        className="mt-1 w-full border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm bg-eh-bg-soft text-eh-muted"
        data-testid={`${testid}-currency`}
      >
        {shown}
      </div>
      {legacy && (
        <div
          className="mt-2 rounded-xl border border-eh-ochre/50 bg-eh-ochre/10 px-3 py-2.5 text-xs text-eh-ink"
          data-testid={`${testid}-currency-notice`}
        >
          <p>{t("catalogue.currency.legacyNotice", { held: held.toUpperCase(), denomination: denomination.toUpperCase() })}</p>
          <button
            type="button"
            onClick={() => onSwitch(denomination)}
            className="btn-ghost text-xs mt-2"
            data-testid={`${testid}-currency-switch`}
          >
            {t("catalogue.currency.switch", { denomination: denomination.toUpperCase() })}
          </button>
        </div>
      )}
    </div>
  );
}

/** True when a row is held in a currency this practice does not price in. `false` while
 *  the practice's currency is unknown or the row names none (the server fills those). */
export function isLegacyCurrency(currency, denomination) {
  const held = (currency || "").toLowerCase();
  return Boolean(held && denomination && held !== denomination.toLowerCase());
}
